const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((subscriber) => subscriber !== callback);
  };
}

function publish(eventName, data) {
  if (!subscribers[eventName]) return;
  subscribers[eventName].forEach((callback) => callback(data));
}

(() => {
  const runtime = document.currentScript;
  if (!runtime) return;

  const prompt = document.querySelector('[data-gpused-pwa-install]');
  if (prompt && !window.Shopify?.designMode) {
    const action = prompt.querySelector('[data-gpused-pwa-action]');
    const close = prompt.querySelector('[data-gpused-pwa-close]');
    const instructions = prompt.querySelector('[data-gpused-pwa-instructions]');

    if (action && close && instructions) {
      const firstSeenKey = 'gpused-pwa-first-seen';
      const dismissedKey = 'gpused-pwa-dismissed';
      const installedKey = 'gpused-pwa-installed';
      const androidDelay = 90 * 1000;
      const iosDelay = 600 * 1000;
      const dismissFor = 30 * 24 * 60 * 60 * 1000;
      const userAgent = window.navigator.userAgent || '';
      const isIos = /iPad|iPhone|iPod/.test(userAgent) ||
        (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
      const isMobile = isIos || /Android/i.test(userAgent);
      const delay = isIos ? iosDelay : androidDelay;
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;

      const readStorage = (key) => {
        try {
          return localStorage.getItem(key);
        } catch (_error) {
          return null;
        }
      };
      const writeStorage = (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch (_error) {
          // The install prompt can still work when storage is unavailable.
        }
      };

      if (isMobile && !isStandalone && readStorage(installedKey) !== '1') {
        const now = Date.now();
        const dismissedAt = Number(readStorage(dismissedKey) || 0);

        if (!dismissedAt || now - dismissedAt >= dismissFor) {
          let firstSeenAt = Number(readStorage(firstSeenKey) || 0);
          if (!firstSeenAt || firstSeenAt > now) {
            firstSeenAt = now;
            writeStorage(firstSeenKey, String(firstSeenAt));
          }

          let installEvent = null;
          let timer = null;
          let instructionsShown = false;

          const canShow = () => isIos || Boolean(installEvent);
          const show = () => {
            if (canShow()) prompt.hidden = false;
          };
          const schedule = () => {
            window.clearTimeout(timer);
            const remaining = delay - (Date.now() - firstSeenAt);
            timer = window.setTimeout(show, Math.max(0, remaining));
          };

          window.addEventListener('beforeinstallprompt', (event) => {
            event.preventDefault();
            installEvent = event;
            if (prompt.dataset.gpusedPwaInstallLabel) {
              action.textContent = prompt.dataset.gpusedPwaInstallLabel;
            }
            schedule();
          });
          window.addEventListener('appinstalled', () => {
            writeStorage(installedKey, '1');
            prompt.hidden = true;
            installEvent = null;
          });
          close.addEventListener('click', () => {
            writeStorage(dismissedKey, String(Date.now()));
            prompt.hidden = true;
          });
          action.addEventListener('click', async () => {
            if (installEvent) {
              installEvent.prompt();
              await installEvent.userChoice;
              installEvent = null;
              prompt.hidden = true;
              return;
            }

            if (instructionsShown) {
              writeStorage(dismissedKey, String(Date.now()));
              prompt.hidden = true;
              return;
            }

            instructionsShown = true;
            instructions.hidden = false;
            if (prompt.dataset.gpusedPwaGotItLabel) {
              action.textContent = prompt.dataset.gpusedPwaGotItLabel;
            }
          });

          if (isIos) {
            if (prompt.dataset.gpusedPwaShowMeLabel) {
              action.textContent = prompt.dataset.gpusedPwaShowMeLabel;
            }
            schedule();
          }
        }
      }
    }
  }

  const featurePromises = new Map();
  const loadFeature = (name, url, data = {}) => {
    if (!url) return Promise.reject(new Error(`${name} URL is missing`));
    if (featurePromises.has(name)) return featurePromises.get(name);

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = url;
      Object.entries(data).forEach(([key, value]) => {
        script.dataset[key] = value || '';
      });
      script.addEventListener('load', () => resolve(script), { once: true });
      script.addEventListener('error', () => reject(new Error(`${name} failed to load`)), {
        once: true,
      });
      document.body.appendChild(script);
    }).catch((error) => {
      featurePromises.delete(name);
      throw error;
    });

    featurePromises.set(name, promise);
    return promise;
  };

  const launcher = document.querySelector('[data-gpused-assistant-launcher]');
  const assistantStatus = document.querySelector('[data-gpused-assistant-status]');
  const assistantData = {
    assistantStyleUrl: runtime.dataset.assistantStyleUrl,
    assistantScriptUrl: runtime.dataset.assistantScriptUrl,
    apiUrl: runtime.dataset.apiUrl,
    mascotUrl: runtime.dataset.mascotUrl,
  };

  const clearAssistantBootstrap = () => {
    launcher?.removeEventListener('pointerenter', prewarmAssistant);
    launcher?.removeEventListener('focus', prewarmAssistant);
    launcher?.removeEventListener('click', openAssistant);
  };
  const loadAssistant = () => loadFeature(
    'GPUsed assistant loader',
    runtime.dataset.assistantLoaderUrl,
    assistantData,
  ).then(() => {
    clearAssistantBootstrap();
    return window.GPUsedAssistantLoader;
  });
  function prewarmAssistant() {
    loadAssistant().catch(() => {});
  }
  function openAssistant() {
    if (launcher) {
      launcher.disabled = true;
      launcher.setAttribute('aria-busy', 'true');
    }
    if (assistantStatus && launcher?.dataset.gpusedAssistantLoading) {
      assistantStatus.textContent = launcher.dataset.gpusedAssistantLoading;
    }

    loadAssistant()
      .then((assistant) => {
        if (!assistant || typeof assistant.open !== 'function') {
          throw new Error('Assistant loader API is unavailable');
        }
        return assistant.open();
      })
      .catch(() => {
        if (assistantStatus && launcher?.dataset.gpusedAssistantError) {
          assistantStatus.textContent = launcher.dataset.gpusedAssistantError;
        }
      })
      .finally(() => {
        if (!launcher) return;
        launcher.disabled = false;
        launcher.setAttribute('aria-busy', 'false');
      });
  }

  launcher?.addEventListener('pointerenter', prewarmAssistant, { passive: true });
  launcher?.addEventListener('focus', prewarmAssistant, { passive: true });
  launcher?.addEventListener('click', openAssistant);

  if (launcher?.dataset.gpusedDeferOnProduct === 'true') {
    const revealThreshold = () => Math.min(280, Math.max(180, window.innerHeight * 0.28));
    const prepareProductAssistant = () => {
      if (window.scrollY < revealThreshold()) return;
      window.removeEventListener('scroll', prepareProductAssistant);
      launcher.hidden = false;
    };
    window.addEventListener('scroll', prepareProductAssistant, { passive: true });
    prepareProductAssistant();
  }

  const prepareBackToTop = () => {
    window.removeEventListener('scroll', prepareBackToTop);
    loadFeature('GPUsed back-to-top control', runtime.dataset.backToTopUrl).catch(() => {});
  };
  window.addEventListener('scroll', prepareBackToTop, { passive: true });
  if (window.scrollY > 0) prepareBackToTop();
})();
